import React, { useState, useEffect } from 'react';
import { toDateInputValue } from '../../lib/dateInputValue';
import { authenticatedFetch } from '../../lib/apiClient';
import { generateContractPdfBase64, generateContractPdfBlob, downloadContractPdf, PartnerContractData } from '../../lib/partnerContractPdf';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  DocumentArrowDownIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  EyeIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

interface PartnerContract {
  id: string;
  contract_number: string;
  title: string;
  contract_type: 'service' | 'consulting' | 'development';
  contract_terms?: string;
  start_date?: string;
  end_date?: string;
  hourly_rate?: number;
  service_types?: Array<{ name: string; hourly_rate?: number; unit?: '小時' | '計次' }> | string[];
  contract_file_url?: string;
  signed_file_url?: string;
  status: 'draft' | 'sent' | 'signed' | 'expired' | 'cancelled';
  sent_date?: string;
  sent_to_email?: string;
  signed_date?: string;
  signed_by_partner: boolean;
  notes?: string;
  created_at: string;
  updated_at: string;
  // 關聯資料
  partner_id: string;
  partner_name?: string;
  partner_english_name?: string;
  partner_email?: string;
  partner_phone?: string;
  project_id: string;
  project_name?: string;
  customer_company?: string;
}

interface Partner {
  id: string;
  name: string;
  email?: string;
}

interface Project {
  id: string;
  name: string;
  customer_id?: string;
}

const PartnerContracts: React.FC = () => {
  const [contracts, setContracts] = useState<PartnerContract[]>([]);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // 篩選狀態
  const [partnerFilter, setPartnerFilter] = useState<string>('');
  const [projectFilter, setProjectFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  
  // 模態框狀態
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedContract, setSelectedContract] = useState<PartnerContract | null>(null);
  const [emailContract, setEmailContract] = useState<PartnerContract | null>(null);
  const [emailData, setEmailData] = useState({
    recipientEmail: '',
    recipientName: '',
    subject: '',
    body: '',
  });
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string>('');
  
  // 技能相關狀態
  const [partnerSkills, setPartnerSkills] = useState<Array<{ name: string; hourly_rate?: number; unit?: string }>>([]);
  const [availableSkills, setAvailableSkills] = useState<string[]>([]);
  
  // 表單狀態
  const [formData, setFormData] = useState({
    partner_id: '',
    project_id: '',
    start_date: '',
    end_date: '',
    notes: '',
    service_types: [] as Array<{ name: string; hourly_rate?: number; unit?: '小時' | '計次' }>,
  });

  // 載入資料
  useEffect(() => {
    loadContracts();
    loadPartners();
    loadProjects();
    loadPartnerSkills();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 當選擇承攬夥伴時，載入該夥伴的技能
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (formData.partner_id) {
      loadPartnerSkillsForPartner(formData.partner_id);
    } else {
      setAvailableSkills([]);
      setFormData(prev => ({ ...prev, service_types: [] }));
    }
  }, [formData.partner_id]);

  const loadContracts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (partnerFilter) params.append('partner_id', partnerFilter);
      if (projectFilter) params.append('project_id', projectFilter);
      if (statusFilter) params.append('status', statusFilter);
      
      const response = await authenticatedFetch(`/api/partner-contracts?${params.toString()}`);
      const data = await response.json();
      
      if (data.success) {
        // 正規化 service_types（從資料庫讀取時可能是 JSONB 物件陣列或字串陣列）
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const normalizedContracts = (data.data.contracts || []).map((contract: any) => {
          if (contract.service_types) {
            let normalizedServiceTypes: Array<{ name: string; hourly_rate?: number; unit?: '小時' | '計次' }> = [];
            
            if (Array.isArray(contract.service_types)) {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              normalizedServiceTypes = contract.service_types.map((st: any) => {
                if (typeof st === 'string') {
                  return { name: st };
                } else if (typeof st === 'object' && st !== null) {
                  return {
                    name: st.name || '',
                    hourly_rate: st.hourly_rate,
                    unit: st.unit || '小時'
                  };
                }
                return { name: '' };
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              }).filter((st: any) => st.name);
            }
            
            return {
              ...contract,
              service_types: normalizedServiceTypes
            };
          }
          return contract;
        });
        
        setContracts(normalizedContracts);
        setError(null);
      } else {
        setError(data.error || '載入合約失敗');
      }
    } catch {
      // 載入合約失敗，靜默處理
      setError('載入合約失敗');
    } finally {
      setLoading(false);
    }
  };

  const loadPartners = async () => {
    try {
      const response = await authenticatedFetch('/api/partners');
      const data = await response.json();
      if (data.success) {
        setPartners(data.data || []);
      }
    } catch {
      // 載入承攬夥伴失敗，靜默處理
    }
  };

  const loadProjects = async () => {
    try {
      const response = await authenticatedFetch('/api/projects');
      const data = await response.json();
      if (data.success) {
        setProjects(data.data || []);
      }
    } catch {
      // 載入專案失敗，靜默處理
    }
  };

  // 載入所有技能選項
  const loadPartnerSkills = async () => {
    try {
      const response = await authenticatedFetch('/api/db/query', {
        method: 'POST',
        body: JSON.stringify({
          table: 'partner_skills',
          select: ['name', 'hourly_rate', 'unit'],
          orderBy: [{ field: 'name', direction: 'ASC' }]
        })
      });
      const data = await response.json();
      if (data.success && data.data) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setPartnerSkills(data.data.map((s: any) => ({
          name: s.name,
          hourly_rate: s.hourly_rate,
          unit: s.unit
        })));
      }
    } catch {
      // 載入技能選項失敗，靜默處理
    }
  };

  // 載入特定承攬夥伴的技能
  const loadPartnerSkillsForPartner = async (partnerId: string) => {
    try {
      // 使用通用查詢端點獲取承攬夥伴資料
      const response = await authenticatedFetch(`/api/db/partners/${partnerId}`);
      const data = await response.json();
      if (data.success && data.data) {
        const partner = data.data;
        let skills: string[] = [];
        
        if (partner.skill) {
          let skillData = partner.skill;
          
          // 處理 JSONB 資料（PostgreSQL 返回的格式）
          if (typeof skillData === 'string') {
            try {
              skillData = JSON.parse(skillData);
            } catch {
              skillData = [];
            }
          }
          
          // 處理陣列格式
          if (Array.isArray(skillData)) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            skills = skillData.map((s: any) => {
              if (typeof s === 'string') {
                return s;
              }
              if (typeof s === 'object' && s !== null) {
                return s.name || '';
              }
              return '';
            }).filter(Boolean);
          }
        }
        
        setAvailableSkills(skills);
        // 不預設選中任何技能，讓用戶自行選擇
        setFormData(prev => ({ ...prev, service_types: [] }));
      } else {
        setAvailableSkills([]);
      }
    } catch (err) {
      console.error('❌ 載入承攬夥伴技能失敗:', err);
      setAvailableSkills([]);
    }
  };

  // 預覽合約
  const handlePreview = async () => {
    if (!formData.partner_id || !formData.project_id) {
      alert('請先選擇承攬夥伴和專案');
      return;
    }

    try {
      // 載入印章
      let companyStamp = '';
      try {
        const stampModule = await import('../../lib/stampBase64');
        companyStamp = stampModule.default || '';
      } catch (err) {
        console.warn('無法載入印章:', err);
      }
      
      // 獲取承攬夥伴詳細資訊
      const partnerResponse = await authenticatedFetch(`/api/partners/${formData.partner_id}`);
      const partnerData = await partnerResponse.json();
      
      if (!partnerData.success || !partnerData.data) {
        alert('無法獲取承攬夥伴資訊');
        return;
      }

      const partner = partnerData.data;
      const selectedProject = projects.find(p => p.id === formData.project_id);
      const projectName = selectedProject?.name || '';

      // 準備預覽資料
      const contractData: PartnerContractData = {
        contract_number: '預覽-000000',
        contract_title: `遠程資科有限公司承攬契約書${projectName ? ` - ${projectName}` : ''}`,
        contract_type: 'service',
        start_date: formData.start_date || '',
        end_date: formData.end_date || '',
        hourly_rate: undefined, // 預覽時不顯示時薪
        service_types: formData.service_types.map(st => 
          typeof st === 'string' ? st : st.name
        ) || [],
        partner_name: partner.name || '',
        partner_english_name: partner.english_name || '',
        partner_id_number: partner.id_number || '',
        partner_address: partner.address || '',
        partner_email: partner.email || '',
        partner_phone: partner.phone || '',
        bank_account_name: partner.bank_account_name || '',
        bank_name: partner.bank_name || '',
        bank_branch: partner.bank_branch || '',
        bank_account_number: partner.bank_account_number || '',
        project_name: projectName,
        customer_company: '',
        work_description: formData.notes || projectName || '',
        contract_date: new Date().toLocaleDateString('zh-TW', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        }),
        company_stamp: companyStamp
      };

      // 預覽功能已移除，直接下載 PDF
      await downloadContractPdf(contractData);
    } catch (err) {
      console.error('預覽合約失敗:', err);
      alert('預覽失敗');
    }
  };

  // 建立合約
  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.partner_id || !formData.project_id) {
      alert('請選擇承攬夥伴和專案');
      return;
    }

    if (formData.service_types.length === 0) {
      alert('請至少選擇一個技能並設定單價');
      return;
    }
    
    // 驗證所有選擇的技能都有設定單價
    const skillsWithoutRate = formData.service_types.filter(st => !st.hourly_rate || st.hourly_rate <= 0);
    if (skillsWithoutRate.length > 0) {
      alert(`請為以下技能設定單價：${skillsWithoutRate.map(s => s.name).join('、')}`);
      return;
    }
    
    try {
      // 獲取選中的專案名稱，用於生成標題
      const selectedProject = projects.find(p => p.id === formData.project_id);
      const projectName = selectedProject?.name || '';
      
      // 自動生成標題
      const title = `遠程資科有限公司承攬契約書${projectName ? ` - ${projectName}` : ''}`;
      
      // 將 service_types 轉換為後端需要的格式（JSONB 陣列）
      const serviceTypesForBackend = formData.service_types.map(st => ({
        name: st.name,
        hourly_rate: st.hourly_rate,
        unit: st.unit
      }));
      
      const response = await authenticatedFetch('/api/partner-contracts', {
        method: 'POST',
        body: JSON.stringify({
          partner_id: formData.partner_id,
          project_id: formData.project_id,
          title: title,
          contract_type: 'service', // 預設為服務類型
          start_date: formData.start_date || null,
          end_date: formData.end_date || null,
          service_types: serviceTypesForBackend,
          // hourly_rate 由後端自動從技能中獲取（使用第一個技能的單價）
          notes: formData.notes || null,
        }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        const newContract = data.data;
        
        // 自動生成PDF
        try {
          // 載入印章
          let companyStamp = '';
          try {
            const stampModule = await import('../../lib/stampBase64');
            companyStamp = stampModule.default || '';
          } catch (err) {
            console.warn('無法載入印章:', err);
          }
          
          // 獲取承攬夥伴詳細資訊
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          let partnerDetails: any = {
            name: newContract.partner_name,
            english_name: newContract.partner_english_name,
            email: newContract.partner_email,
            phone: newContract.partner_phone,
            id_number: '',
            address: ''
          };
          
          if (newContract.partner_id) {
            try {
              const partnerResponse = await authenticatedFetch(`/api/partners/${newContract.partner_id}`);
              const partnerData = await partnerResponse.json();
              if (partnerData.success && partnerData.data) {
                partnerDetails = {
                  ...partnerDetails,
                  ...partnerData.data
                };
              }
            } catch (err) {
              console.warn('無法獲取承攬夥伴詳細資訊:', err);
            }
          }
          
          // 準備合約資料
          const contractData: PartnerContractData = {
            contract_number: newContract.contract_number,
            contract_title: newContract.title,
            contract_type: newContract.contract_type,
            start_date: newContract.start_date || '',
            end_date: newContract.end_date || '',
            hourly_rate: newContract.hourly_rate || undefined,
            service_types: Array.isArray(newContract.service_types) 
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              ? newContract.service_types.map((st: any) => {
                  if (typeof st === 'string') {
                    return st;
                  } else if (typeof st === 'object' && st !== null) {
                    return st.name || '';
                  }
                  return '';
                }).filter(Boolean)
              : [],
            partner_name: newContract.partner_name || partnerDetails.name || '',
            partner_english_name: partnerDetails.english_name || '',
            partner_id_number: partnerDetails.id_number || '',
            partner_address: partnerDetails.address || '',
            partner_email: newContract.partner_email || partnerDetails.email || '',
            partner_phone: partnerDetails.phone || '',
            bank_account_name: partnerDetails.bank_account_name || '',
            bank_name: partnerDetails.bank_name || '',
            bank_branch: partnerDetails.bank_branch || '',
            bank_account_number: partnerDetails.bank_account_number || '',
            project_name: newContract.project_name || '',
            customer_company: newContract.customer_company || '',
            work_description: newContract.notes || newContract.project_name || '',
            contract_date: new Date().toLocaleDateString('zh-TW', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit'
            }),
            company_stamp: companyStamp
          };
          
          // 生成 PDF Base64
          const pdfBase64 = await generateContractPdfBase64(contractData);
          
          // 上傳到後端
          const pdfResponse = await authenticatedFetch(`/api/partner-contracts/${newContract.id}/generate-pdf`, {
            method: 'POST',
            body: JSON.stringify({ pdfBase64 }),
          });
          
          const pdfData = await pdfResponse.json();
          if (!pdfData.success) {
            console.warn('PDF 生成失敗:', pdfData.error);
          }
        } catch (err) {
          console.error('自動生成PDF失敗:', err);
          // PDF生成失敗不影響合約建立
        }
        
        alert('合約建立成功！PDF已自動生成。');
        setShowCreateModal(false);
        resetForm();
        loadContracts();
      } else {
        alert(`建立失敗: ${data.error || data.message}`);
      }
    } catch (err) {
      console.error('建立合約失敗:', err);
      alert('建立失敗');
    }
  };

  // 更新合約（僅可更新日期和備註）
  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedContract) return;
    
    try {
      const response = await authenticatedFetch(`/api/partner-contracts/${selectedContract.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          start_date: formData.start_date || null,
          end_date: formData.end_date || null,
          notes: formData.notes,
        }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        alert('合約更新成功！');
        setShowEditModal(false);
        setSelectedContract(null);
        resetForm();
        loadContracts();
      } else {
        alert(`更新失敗: ${data.error || data.message}`);
      }
    } catch (err) {
      console.error('更新合約失敗:', err);
      alert('更新失敗');
    }
  };

  // 刪除合約
  const handleDelete = async () => {
    if (!selectedContract) return;
    
    try {
      const response = await authenticatedFetch(`/api/partner-contracts/${selectedContract.id}`, {
        method: 'DELETE',
      });
      
      const data = await response.json();
      
      if (data.success) {
        alert('合約刪除成功！');
        setShowDeleteModal(false);
        setSelectedContract(null);
        loadContracts();
      } else {
        alert(`刪除失敗: ${data.error || data.message}`);
      }
    } catch (err) {
      console.error('刪除合約失敗:', err);
      alert('刪除失敗');
    }
  };


  // 開啟寄送郵件模態框
  const openEmailModal = (contract: PartnerContract) => {
    setEmailContract(contract);
    setEmailData({
      recipientEmail: contract.partner_email || '',
      recipientName: contract.partner_name || '',
      subject: `承攬合約 ${contract.contract_number} - ${contract.title}`,
      body: `
        <h2>承攬合約 ${contract.contract_number}</h2>
        <p>親愛的 ${contract.partner_name || '承攬夥伴'}，</p>
        <p>附件為您的承攬合約，請查收並簽名後回傳。</p>
        <p><strong>合約編號：</strong>${contract.contract_number}</p>
        <p><strong>合約標題：</strong>${contract.title}</p>
        <p><strong>專案名稱：</strong>${contract.project_name || '-'}</p>
        ${contract.start_date ? `<p><strong>開始日期：</strong>${contract.start_date}</p>` : ''}
        ${contract.end_date ? `<p><strong>結束日期：</strong>${contract.end_date}</p>` : ''}
        <p>請下載附件中的合約 PDF，簽名後上傳至系統。</p>
        <p>如有任何問題，請隨時與我們聯繫。</p>
        <p>謝謝！</p>
        <p>遠程資科有限公司</p>
      `,
    });
    setShowEmailModal(true);
    
    // 自動載入 PDF 預覽
    setTimeout(() => {
      handlePreviewContractPdf();
    }, 500);
  };

  // 預覽合約PDF（用於郵件模態框）
  const handlePreviewContractPdf = async () => {
    if (!emailContract) return;
    
    try {
      // 載入印章
      let companyStamp = '';
      try {
        const stampModule = await import('../../lib/stampBase64');
        companyStamp = stampModule.default || '';
      } catch (err) {
        console.warn('無法載入印章:', err);
      }
      
      // 獲取承攬夥伴詳細資訊
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let partnerDetails: any = {
        name: emailContract.partner_name,
        english_name: emailContract.partner_english_name,
        email: emailContract.partner_email,
        phone: emailContract.partner_phone,
        id_number: '',
        address: ''
      };
      
      if (emailContract.partner_id) {
        try {
          const partnerResponse = await authenticatedFetch(`/api/partners/${emailContract.partner_id}`);
          const partnerData = await partnerResponse.json();
          if (partnerData.success && partnerData.data) {
            partnerDetails = {
              ...partnerDetails,
              ...partnerData.data
            };
          }
        } catch (err) {
          console.warn('無法獲取承攬夥伴詳細資訊:', err);
        }
      }
      
      // 準備合約資料
      const contractData: PartnerContractData = {
        contract_number: emailContract.contract_number,
        contract_title: emailContract.title,
        contract_type: emailContract.contract_type,
        start_date: emailContract.start_date || '',
        end_date: emailContract.end_date || '',
        hourly_rate: emailContract.hourly_rate || undefined,
        service_types: Array.isArray(emailContract.service_types) 
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ? emailContract.service_types.map((st: any) => {
              if (typeof st === 'string') {
                return st;
              } else if (typeof st === 'object' && st !== null) {
                return st.name || '';
              }
              return '';
            }).filter(Boolean)
          : [],
        partner_name: emailContract.partner_name || partnerDetails.name || '',
        partner_english_name: partnerDetails.english_name || '',
        partner_id_number: partnerDetails.id_number || '',
        partner_address: partnerDetails.address || '',
        partner_email: emailContract.partner_email || partnerDetails.email || '',
        partner_phone: partnerDetails.phone || '',
        bank_account_name: partnerDetails.bank_account_name || '',
        bank_name: partnerDetails.bank_name || '',
        bank_branch: partnerDetails.bank_branch || '',
        bank_account_number: partnerDetails.bank_account_number || '',
        project_name: emailContract.project_name || '',
        customer_company: emailContract.customer_company || '',
        work_description: emailContract.notes || emailContract.project_name || '',
        contract_date: new Date().toLocaleDateString('zh-TW', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        }),
        company_stamp: companyStamp
      };
      
      // 生成 PDF blob
      const pdfBlob = await generateContractPdfBlob(contractData);
      const pdfUrl = URL.createObjectURL(pdfBlob);
      setPdfPreviewUrl(pdfUrl);
      setShowPdfPreview(true);
    } catch (err) {
      console.error('生成 PDF 預覽失敗:', err);
      alert('生成 PDF 預覽失敗，請稍後再試');
    }
  };

  // 下載合約PDF
  const handleDownloadPdf = async (contract: PartnerContract) => {
    try {
      // 載入印章
      let companyStamp = '';
      try {
        const stampModule = await import('../../lib/stampBase64');
        companyStamp = stampModule.default || '';
      } catch (err) {
        console.warn('無法載入印章:', err);
      }
      
      // 獲取承攬夥伴詳細資訊
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let partnerDetails: any = {
        name: contract.partner_name,
        english_name: contract.partner_english_name,
        email: contract.partner_email,
        phone: contract.partner_phone,
        id_number: '',
        address: ''
      };
      
      if (contract.partner_id) {
        try {
          const partnerResponse = await authenticatedFetch(`/api/partners/${contract.partner_id}`);
          const partnerData = await partnerResponse.json();
          if (partnerData.success && partnerData.data) {
            partnerDetails = {
              ...partnerDetails,
              ...partnerData.data
            };
          }
        } catch (err) {
          console.warn('無法獲取承攬夥伴詳細資訊:', err);
        }
      }
      
      // 準備合約資料
      const contractData: PartnerContractData = {
        contract_number: contract.contract_number,
        contract_title: contract.title,
        contract_type: contract.contract_type,
        start_date: contract.start_date || '',
        end_date: contract.end_date || '',
        hourly_rate: contract.hourly_rate || undefined,
        service_types: Array.isArray(contract.service_types) 
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ? contract.service_types.map((st: any) => {
              if (typeof st === 'string') {
                return st;
              } else if (typeof st === 'object' && st !== null) {
                return st.name || '';
              }
              return '';
            }).filter(Boolean)
          : [],
        partner_name: contract.partner_name || partnerDetails.name || '',
        partner_english_name: partnerDetails.english_name || '',
        partner_id_number: partnerDetails.id_number || '',
        partner_address: partnerDetails.address || '',
        partner_email: contract.partner_email || partnerDetails.email || '',
        partner_phone: partnerDetails.phone || '',
        bank_account_name: partnerDetails.bank_account_name || '',
        bank_name: partnerDetails.bank_name || '',
        bank_branch: partnerDetails.bank_branch || '',
        bank_account_number: partnerDetails.bank_account_number || '',
        project_name: contract.project_name || '',
        customer_company: contract.customer_company || '',
        work_description: contract.notes || contract.project_name || '',
        contract_date: new Date().toLocaleDateString('zh-TW', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        }),
        company_stamp: companyStamp
      };
      
      await downloadContractPdf(contractData);
    } catch (err) {
      console.error('下載PDF失敗:', err);
      alert('下載PDF失敗');
    }
  };

  // 發送郵件
  const handleSendEmail = async () => {
    if (!emailContract || !emailData.recipientEmail) {
      alert('請填寫收件人郵件地址');
      return;
    }

    setIsSendingEmail(true);

    try {
      // 合約建立時已自動生成PDF，這裡直接使用預覽的PDF
      // 如果沒有PDF URL，使用預覽生成的PDF

      // 發送郵件
      const response = await authenticatedFetch(`/api/partner-contracts/${emailContract.id}/send`, {
        method: 'POST',
        body: JSON.stringify({
          recipient_email: emailData.recipientEmail,
          subject: emailData.subject,
          message: emailData.body,
        }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        alert('郵件發送成功！');
        setShowEmailModal(false);
        setEmailContract(null);
        setEmailData({ recipientEmail: '', recipientName: '', subject: '', body: '' });
        loadContracts();
      } else {
        alert(`郵件發送失敗: ${data.error || data.message}`);
      }
    } catch (err) {
      console.error('發送郵件失敗:', err);
      alert('發送郵件失敗');
    } finally {
      setIsSendingEmail(false);
    }
  };

  // 重置表單
  const resetForm = () => {
    setFormData({
      partner_id: '',
      project_id: '',
      start_date: '',
      end_date: '',
      notes: '',
      service_types: [],
    });
    setAvailableSkills([]);
  };

  // 處理技能選擇
  const handleSkillToggle = (skillName: string) => {
    const skillInfo = partnerSkills.find(s => s.name === skillName);
    const currentServiceTypes = formData.service_types || [];
    const isSelected = currentServiceTypes.some(st => st.name === skillName);
    
    if (isSelected) {
      // 移除技能
      setFormData(prev => ({
        ...prev,
        service_types: prev.service_types.filter(st => st.name !== skillName)
      }));
    } else {
      // 添加技能，使用預設單價和計價方式
      setFormData(prev => ({
        ...prev,
        service_types: [
          ...prev.service_types,
          {
            name: skillName,
            hourly_rate: skillInfo?.hourly_rate,
            unit: (skillInfo?.unit || '小時') as '小時' | '計次'
          }
        ]
      }));
    }
  };

  // 處理技能單價變更
  const handleSkillRateChange = (skillName: string, hourlyRate: number | undefined) => {
    setFormData(prev => ({
      ...prev,
      service_types: prev.service_types.map(st =>
        st.name === skillName ? { ...st, hourly_rate: hourlyRate } : st
      )
    }));
  };

  // 處理技能計價方式變更
  const handleSkillUnitChange = (skillName: string, unit: '小時' | '計次') => {
    setFormData(prev => ({
      ...prev,
      service_types: prev.service_types.map(st =>
        st.name === skillName ? { ...st, unit } : st
      )
    }));
  };

  // 開啟編輯模態框
  const openEditModal = (contract: PartnerContract) => {
    if (contract.status !== 'draft') {
      alert('只能編輯草稿狀態的合約');
      return;
    }
    
    setSelectedContract(contract);
    setFormData({
      partner_id: contract.partner_id,
      project_id: contract.project_id,
      start_date: toDateInputValue(contract.start_date),
      end_date: toDateInputValue(contract.end_date),
      notes: contract.notes || '',
      service_types: Array.isArray(contract.service_types) 
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ? contract.service_types.map((st: any) => {
            if (typeof st === 'string') {
              return { name: st };
            } else if (typeof st === 'object' && st !== null) {
              return {
                name: st.name || '',
                hourly_rate: st.hourly_rate,
                unit: st.unit || '小時'
              };
            }
            return { name: '' };
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          }).filter((st: any) => st.name)
        : [],
    });
    setShowEditModal(true);
  };

  // 開啟刪除確認
  const openDeleteModal = (contract: PartnerContract) => {
    if (contract.status !== 'draft') {
      alert('只能刪除草稿狀態的合約');
      return;
    }
    
    setSelectedContract(contract);
    setShowDeleteModal(true);
  };

  // 狀態名稱
  const statusNames: Record<string, string> = {
    draft: '草稿',
    sent: '已發送',
    signed: '已簽名',
    expired: '已過期',
    cancelled: '已取消',
  };

  // 狀態顏色
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'sent':
        return 'bg-blue-100 text-blue-800';
      case 'signed':
        return 'bg-green-100 text-green-800';
      case 'expired':
        return 'bg-red-100 text-red-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // 統計數據
  const stats = {
    total: contracts.length,
    draft: contracts.filter(c => c.status === 'draft').length,
    sent: contracts.filter(c => c.status === 'sent').length,
    signed: contracts.filter(c => c.status === 'signed').length,
  };

  return (
    <div className="p-6">
      {/* 標題 */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <DocumentTextIcon className="w-8 h-8 mr-2 text-blue-600" />
          承攬合約管理
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          管理與承攬夥伴簽訂的專案合約
        </p>
      </div>

      {/* 統計卡片 */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-500">總合約數</div>
          <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg shadow">
          <div className="text-sm text-gray-600">草稿</div>
          <div className="text-2xl font-bold text-gray-900">{stats.draft}</div>
        </div>
        <div className="bg-blue-50 p-4 rounded-lg shadow">
          <div className="text-sm text-blue-600">已發送</div>
          <div className="text-2xl font-bold text-blue-900">{stats.sent}</div>
        </div>
        <div className="bg-green-50 p-4 rounded-lg shadow">
          <div className="text-sm text-green-600">已簽名</div>
          <div className="text-2xl font-bold text-green-900">{stats.signed}</div>
        </div>
      </div>

      {/* 操作列 */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          {/* 篩選 */}
          <div className="flex gap-2 flex-wrap">
            <select
              value={partnerFilter}
              onChange={(e) => setPartnerFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="">全部承攬夥伴</option>
              {partners.map(partner => (
                <option key={partner.id} value={partner.id}>
                  {partner.name}
                </option>
              ))}
            </select>

            <select
              value={projectFilter}
              onChange={(e) => setProjectFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="">全部專案</option>
              {projects.map(project => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="">全部狀態</option>
              <option value="draft">草稿</option>
              <option value="sent">已發送</option>
              <option value="signed">已簽名</option>
              <option value="expired">已過期</option>
              <option value="cancelled">已取消</option>
            </select>
          </div>

          {/* 新增按鈕 */}
          <button
            onClick={() => {
              resetForm();
              setShowCreateModal(true);
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 whitespace-nowrap"
          >
            <PlusIcon className="w-5 h-5" />
            新增合約
          </button>
        </div>
      </div>

      {/* 錯誤訊息 */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {/* 合約列表 */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">載入中...</p>
          </div>
        ) : contracts.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <DocumentTextIcon className="w-12 h-12 mx-auto mb-2 text-gray-400" />
            <p>沒有找到合約</p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  合約編號
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  標題
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  承攬夥伴
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  專案
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  狀態
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  簽名狀態
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  操作
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {contracts.map((contract) => (
                <tr key={contract.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {contract.contract_number}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {contract.title}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {contract.partner_name || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {contract.project_name || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(contract.status)}`}>
                      {statusNames[contract.status]}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {contract.signed_by_partner ? (
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 flex items-center gap-1 w-fit">
                        <CheckCircleIcon className="w-3 h-3" />
                        已簽名
                      </span>
                    ) : (
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800 flex items-center gap-1 w-fit">
                        <ClockIcon className="w-3 h-3" />
                        待簽名
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      {contract.status === 'draft' && (
                        <>
                          <button
                            onClick={() => openEditModal(contract)}
                            className="text-blue-600 hover:text-blue-900"
                            title="編輯"
                          >
                            <PencilIcon className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => openDeleteModal(contract)}
                            className="text-red-600 hover:text-red-900"
                            title="刪除"
                          >
                            <TrashIcon className="w-5 h-5" />
                          </button>
                        </>
                      )}
                      {contract.status === 'draft' && (
                        <>
                          {contract.contract_file_url && (
                            <button
                              onClick={() => handleDownloadPdf(contract)}
                              className="text-green-600 hover:text-green-900"
                              title="下載 PDF"
                            >
                              <DocumentArrowDownIcon className="w-5 h-5" />
                            </button>
                          )}
                          <button
                            onClick={() => openEmailModal(contract)}
                            className="text-purple-600 hover:text-purple-900"
                            title="寄送郵件"
                          >
                            <PaperAirplaneIcon className="w-5 h-5" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* 建立合約模態框 */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-lg bg-white">
            <div className="mb-4">
              <h3 className="text-lg font-bold text-gray-900 flex items-center">
                <PlusIcon className="w-6 h-6 mr-2 text-blue-600" />
                新增承攬合約
              </h3>
            </div>

            <form onSubmit={handleCreate} className="space-y-4">
              {/* 承攬夥伴 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  承攬夥伴 <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  value={formData.partner_id}
                  onChange={(e) => setFormData({ ...formData, partner_id: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="">請選擇承攬夥伴</option>
                  {partners.map(partner => (
                    <option key={partner.id} value={partner.id}>
                      {partner.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* 專案 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  專案 <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  value={formData.project_id}
                  onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="">請選擇專案</option>
                  {projects.map(project => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  合約標題將自動生成為「遠程資科有限公司承攬契約書 - {projects.find(p => p.id === formData.project_id)?.name || '專案名稱'}」
                </p>
              </div>

              {/* 技能選擇 */}
              {formData.partner_id && availableSkills.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    服務技能與單價 <span className="text-red-500">*</span>
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    請選擇此合約中會使用到的技能，並為每個技能設定單價和計價方式
                  </p>
                  <div className="border border-gray-300 rounded-lg p-3 max-h-96 overflow-y-auto">
                    <div className="space-y-3">
                      {availableSkills.map((skillName) => {
                        const skillInfo = partnerSkills.find(s => s.name === skillName);
                        const selectedSkill = formData.service_types.find(st => st.name === skillName);
                        const isSelected = !!selectedSkill;
                        
                        return (
                          <div
                            key={skillName}
                            className={`border rounded-lg p-3 ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}
                          >
                            <label className="flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleSkillToggle(skillName)}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="ml-2 text-sm font-medium text-gray-700 flex-1">{skillName}</span>
                              {skillInfo?.hourly_rate && !isSelected && (
                                <span className="text-xs text-gray-500">
                                  （預設：{skillInfo.hourly_rate.toLocaleString()} 元/{skillInfo.unit || '小時'}）
                                </span>
                              )}
                            </label>
                            {isSelected && (
                              <div className="mt-3 grid grid-cols-2 gap-3 ml-6">
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">
                                    單價（元）*
                                  </label>
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={selectedSkill?.hourly_rate ?? skillInfo?.hourly_rate ?? ''}
                                    onChange={(e) => handleSkillRateChange(skillName, e.target.value ? parseFloat(e.target.value) : undefined)}
                                    className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm"
                                    placeholder={skillInfo?.hourly_rate?.toString() || '請輸入單價'}
                                    required
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">
                                    計價方式 *
                                  </label>
                                  <select
                                    value={selectedSkill?.unit ?? skillInfo?.unit ?? '小時'}
                                    onChange={(e) => handleSkillUnitChange(skillName, e.target.value as '小時' | '計次')}
                                    className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm"
                                    required
                                  >
                                    <option value="小時">小時</option>
                                    <option value="計次">計次</option>
                                  </select>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  {formData.service_types.length === 0 && (
                    <p className="mt-1 text-xs text-red-500">請至少選擇一個技能並設定單價</p>
                  )}
                </div>
              )}
              
              {/* 如果沒有可用技能，顯示提示 */}
              {formData.partner_id && availableSkills.length === 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-sm text-yellow-800">
                    ⚠️ 此承攬夥伴尚未設定技能，請先在「承攬夥伴管理」中為該夥伴設定技能。
                  </p>
                </div>
              )}

              {/* 日期 */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    開始日期
                  </label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    結束日期
                  </label>
                  <input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <strong>注意：</strong>時薪將自動從承攬夥伴的技能中取得。請確保已為該承攬夥伴設定技能和時薪。
                </p>
              </div>

              {/* 備註 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  備註
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  rows={3}
                  placeholder="輸入備註..."
                />
              </div>

              {/* 按鈕 */}
              <div className="flex gap-2 justify-end mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  取消
                </button>
                <button
                  type="button"
                  onClick={handlePreview}
                  className="px-4 py-2 border border-blue-300 rounded-lg text-blue-700 hover:bg-blue-50 flex items-center gap-2"
                >
                  <EyeIcon className="w-4 h-4" />
                  預覽
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
                >
                  建立
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 編輯合約模態框 */}
      {showEditModal && selectedContract && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-lg bg-white">
            <div className="mb-4">
              <h3 className="text-lg font-bold text-gray-900 flex items-center">
                <PencilIcon className="w-6 h-6 mr-2 text-blue-600" />
                編輯合約
              </h3>
              <p className="text-sm text-gray-500 mt-1">{selectedContract.contract_number}</p>
            </div>

            <form onSubmit={handleUpdate} className="space-y-4">
              {/* 顯示合約資訊（唯讀） */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-2">
                  <strong>合約編號：</strong>{selectedContract.contract_number}
                </p>
                <p className="text-sm text-gray-600 mb-2">
                  <strong>合約標題：</strong>{selectedContract.title}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>承攬夥伴：</strong>{selectedContract.partner_name}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>專案：</strong>{selectedContract.project_name}
                </p>
              </div>

              {/* 日期 */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    開始日期
                  </label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    結束日期
                  </label>
                  <input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
              </div>

              {/* 備註 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  備註
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  rows={3}
                  placeholder="輸入備註..."
                />
              </div>

              <div className="flex gap-2 justify-end mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedContract(null);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  取消
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
                >
                  更新
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 寄送郵件模態框 */}
      {showEmailModal && emailContract && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">寄送承攬合約郵件</h2>
              <button
                onClick={() => {
                  setShowEmailModal(false);
                  setEmailContract(null);
                  setEmailData({ recipientEmail: '', recipientName: '', subject: '', body: '' });
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* 左側：郵件設定 */}
              <div className="space-y-4">
                {/* 合約資訊 */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">合約資訊</h4>
                  <div className="text-sm text-gray-600 space-y-1">
                    <p><strong>合約編號：</strong>{emailContract.contract_number}</p>
                    <p><strong>合約標題：</strong>{emailContract.title}</p>
                    <p><strong>承攬夥伴：</strong>{emailContract.partner_name || '-'}</p>
                    <p><strong>專案名稱：</strong>{emailContract.project_name || '-'}</p>
                    {emailContract.start_date && (
                      <p><strong>開始日期：</strong>{emailContract.start_date}</p>
                    )}
                    {emailContract.end_date && (
                      <p><strong>結束日期：</strong>{emailContract.end_date}</p>
                    )}
                  </div>
                </div>

                {/* 收件人資訊 */}
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      收件人郵件地址 *
                    </label>
                    <input
                      type="email"
                      required
                      value={emailData.recipientEmail}
                      onChange={(e) => setEmailData({...emailData, recipientEmail: e.target.value})}
                      placeholder="example@company.com"
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      收件人姓名
                    </label>
                    <input
                      type="text"
                      value={emailData.recipientName}
                      onChange={(e) => setEmailData({...emailData, recipientName: e.target.value})}
                      placeholder="收件人姓名"
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                    />
                  </div>
                </div>

                {/* 郵件主旨 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    郵件主旨
                  </label>
                  <input
                    type="text"
                    value={emailData.subject}
                    onChange={(e) => setEmailData({...emailData, subject: e.target.value})}
                    placeholder="郵件主旨"
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>

                {/* 郵件內容 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    郵件內容
                  </label>
                  <textarea
                    value={emailData.body}
                    onChange={(e) => setEmailData({...emailData, body: e.target.value})}
                    rows={8}
                    placeholder="郵件內容"
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>

                {/* 操作按鈕 */}
                <div className="flex flex-col space-y-2 pt-4">
                  <button
                    onClick={handleSendEmail}
                    disabled={isSendingEmail}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {isSendingEmail ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        發送中...
                      </>
                    ) : (
                      <>
                        <PaperAirplaneIcon className="w-4 h-4 mr-2" />
                        寄送郵件
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setShowEmailModal(false);
                      setEmailContract(null);
                      setEmailData({ recipientEmail: '', recipientName: '', subject: '', body: '' });
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    取消
                  </button>
                </div>
              </div>

              {/* 右側：PDF 預覽 */}
              <div>
                <div className="bg-gray-50 rounded-lg p-4 h-full">
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="text-sm font-medium text-gray-900">PDF 預覽</h4>
                    <button
                      onClick={handlePreviewContractPdf}
                      className="px-3 py-1 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 flex items-center"
                    >
                      <EyeIcon className="w-4 h-4 mr-1" />
                      重新載入
                    </button>
                  </div>
                  {showPdfPreview && pdfPreviewUrl ? (
                    <div className="w-full h-[600px] border border-gray-300 rounded-lg overflow-hidden bg-white">
                      <iframe
                        src={pdfPreviewUrl}
                        className="w-full h-full"
                        title="PDF 預覽"
                      />
                    </div>
                  ) : (
                    <div className="w-full h-[600px] border border-gray-300 rounded-lg flex items-center justify-center bg-white">
                      <div className="text-center text-gray-500">
                        <DocumentTextIcon className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                        <p className="text-lg font-medium mb-2">PDF 預覽</p>
                        <p className="text-sm">正在載入合約 PDF...</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 刪除確認模態框 */}
      {showDeleteModal && selectedContract && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-lg bg-white">
            <div className="mb-4">
              <h3 className="text-lg font-bold text-gray-900 flex items-center text-red-600">
                <TrashIcon className="w-6 h-6 mr-2" />
                刪除合約
              </h3>
            </div>

            <div className="mb-6">
              <p className="text-gray-700 mb-2">確定要刪除以下合約嗎？</p>
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="font-medium text-gray-900">{selectedContract.contract_number}</p>
                <p className="text-sm text-gray-500">{selectedContract.title}</p>
              </div>
              <p className="text-sm text-red-600 mt-3">
                ⚠️ 此操作無法復原。
              </p>
            </div>

            <div className="flex gap-2 justify-end">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedContract(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                取消
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg"
              >
                確定刪除
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerContracts;

